import React from 'react';
import styled, { css } from 'styled-components';

import CalmIcon from 'assets/effects/calm';
import ClearMindIcon from 'assets/effects/clear-mind';
import CreativeIcon from 'assets/effects/creative';
import EnergeticIcon from 'assets/effects/energetic';
import FocusedIcon from 'assets/effects/focused';
import HappyIcon from 'assets/effects/happy';
import InspiredIcon from 'assets/effects/inspired';
import RelaxedIcon from 'assets/effects/relaxed';
import SleepyIcon from 'assets/effects/sleepy';
import UpliftedIcon from 'assets/effects/uplifted';

import { REVIEW_EFFECTS, ReviewEffect, getEffectLabel } from './effects';

/** Shared effect SVGs from `assets/effects/*` — the same icons the product-details Effects section renders. */
type EffectSvg = React.ComponentType<{ height?: string }>;

export const EFFECT_ICONS: Record<ReviewEffect, EffectSvg> = {
  calm: CalmIcon,
  clearMind: ClearMindIcon,
  creative: CreativeIcon,
  energetic: EnergeticIcon,
  focused: FocusedIcon,
  happy: HappyIcon,
  inspired: InspiredIcon,
  relaxed: RelaxedIcon,
  sleepy: SleepyIcon,
  uplifted: UpliftedIcon,
};

const isReviewEffect = (effect: string): effect is ReviewEffect =>
  (REVIEW_EFFECTS as readonly string[]).includes(effect);

/** Icon + label for a single effect; tolerant of unknown effect strings from the API. */
export const renderEffectIcon = (effect: string, height = '16px'): JSX.Element | null => {
  if (!isReviewEffect(effect)) return null;
  const Icon = EFFECT_ICONS[effect];
  return <Icon height={height} />;
};

/** Translated label, falling back to the raw effect string for values outside `REVIEW_EFFECTS`. */
export const effectLabel = (effect: string): string => (isReviewEffect(effect) ? getEffectLabel(effect) : effect);

type EffectChipProps = {
  effect: string;
  /** Optional mention count, e.g. `22` renders "Sleepy (22)". */
  count?: number;
  /** `outlined` = bordered pill (e.g. "Top effects mentioned"); `plain` = borderless inline (e.g. per-review). */
  variant?: 'outlined' | 'plain';
  className?: string;
};

/**
 * Read-only presentational effect chip. Shares the icon map and labels with
 * `EffectsChipsInput` so the display and submission surfaces stay in sync.
 */
export const EffectChip = ({ effect, count, variant = 'outlined', className }: EffectChipProps): JSX.Element => (
  <Chip $variant={variant} className={className}>
    {renderEffectIcon(effect)}
    {effectLabel(effect)}
    {count != null && ` (${count})`}
  </Chip>
);

const Chip = styled.span<{ $variant: 'outlined' | 'plain' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  ${({ $variant, theme }) =>
    $variant === 'outlined'
      ? css`
          height: 36px;
          padding: 0 14px;
          border-radius: 18px;
          border: 1px solid ${theme.colors.grey[80]};
          background: ${theme.colors.white};
          color: ${theme.colors.grey[30]};
          font-size: 14px;
        `
      : css`
          color: ${theme.colors.grey[45]};
          font-size: 13px;
        `}
`;
